import React from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
  Link,
} from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "react-hot-toast";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import "./styles/mobile-safe.css";

// Components
import LandingPage from "./components/landing/LandingPage";
import MapsInformation from "./components/context/MapsInformation";
import Login from "./components/auth/Login";
import Register from "./components/auth/Register";
import ForgotPassword from "./components/auth/ForgotPassword";
import ResetPassword from "./components/auth/ResetPassword";
import ProfilePage from "./components/dashboard/ProfilePage";
import ProtectedRoute from "./components/common/ProtectedRoute";
import BottomTabNav from "./components/ui/BottomTabNav";
import WorkspaceMenu from "./components/ui/WorkspaceMenu";
import EventQuickCreateModal from "./components/events/EventQuickCreateModal";
import NoteQuickCaptureModal from "./components/notes/NoteQuickCaptureModal";
import AIChatInterface from "./components/ai/AIChatInterface.jsx";
import NotesList from "./components/notes/NotesList.js";
import Calendar from "./components/events/Calendar";
import MeetingPage from "./components/events/MeetingPage";
import CalendarChangePage from "./components/events/CalendarChangePage";
import TrackerPage from "./components/assistant/TrackerPage";
import MailPage from "./components/assistant/MailPage";
import PlansPage from "./components/context/PlansPage";
import SocialPage from "./components/assistant/SocialPage";
import DocumentsPage from "./components/assistant/DocumentsPage";
import GoogleIntegration from "./components/integrations/GoogleIntegration.jsx";
import TodayPage from "./components/context/TodayPage.jsx";
import ContextsPage from "./components/context/ContextsPage.jsx";
import ContextDetailPage from "./components/context/ContextDetailPage.jsx";
import InboxPage from "./components/context/InboxPage.jsx";
import UniversalCaptureSheet from "./components/context/UniversalCaptureSheet.jsx";
import AssistantPage from "./components/assistant/AssistantPage";
import LearningPage from "./components/learning/LearningPage";
import ChallengesPage from "./components/challenges/ChallengesPage";
import GoalsPage from "./components/workspace/GoalsPage";
import WorkDock from "./components/workspace/WorkDock";
import WorkspaceSearch from "./components/workspace/WorkspaceSearch";
import AssistantPreferences from "./components/workspace/AssistantPreferences";
import { WorkspaceRefresh } from "./components/workspace/useWorkspace";
import { workspaceTitle } from "./components/workspace/workspaces";
import "./components/workspace/Workspace.css";

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      cacheTime: 10 * 60 * 1000, // 10 minutes
    },
  },
});

function Shell() {
  const location = useLocation();
  const { user } = useAuth();
  const [expanded, setExpanded] = React.useState(false);
  const [captureOpen, setCaptureOpen] = React.useState(false);
  const [captureContextId, setCaptureContextId] = React.useState(null);
  const [eventOpen, setEventOpen] = React.useState(false);
  const [eventDate, setEventDate] = React.useState(null);
  const [noteOpen, setNoteOpen] = React.useState(false);

  const hideChrome = [
    "/",
    "/login",
    "/register",
    "/forgot-password",
    "/reset-password",
    "/maps-information",
  ].includes(location.pathname);
  const isConversation = location.pathname === "/ai";
  const isWorkspace =
    !hideChrome &&
    ![
      "/ai",
      "/today",
      "/goals",
      "/library",
      "/more",
      "/assistant",
      "/dashboard",
      "/dashboard/classic",
    ].includes(location.pathname);
  React.useEffect(() => setExpanded(false), [location.pathname]);

  React.useEffect(() => {
    const handler = (e) => {
      setEventDate(e.detail?.date || null);
      setEventOpen(true);
    };
    window.addEventListener("timelyzer:quick-event", handler);
    return () => window.removeEventListener("timelyzer:quick-event", handler);
  }, []);

  React.useEffect(() => {
    const handler = () => {
      setNoteOpen(true);
    };
    window.addEventListener("timelyzer:quick-note", handler);
    return () => window.removeEventListener("timelyzer:quick-note", handler);
  }, []);

  React.useEffect(() => {
    const handler = (event) => {
      setCaptureContextId(event.detail?.contextId || null);
      setCaptureOpen(true);
    };
    window.addEventListener("timelyzer:universal-capture", handler);
    return () =>
      window.removeEventListener("timelyzer:universal-capture", handler);
  }, []);

  return (
    <div className="min-h-screen bg-[#f4f6f3]">
      <div
        className={
          hideChrome
            ? ""
            : `unified-content ${isConversation || isWorkspace ? "with-conversation" : ""} ${isWorkspace ? "with-workspace" : ""} ${expanded ? "workspace-expanded" : ""}`
        }
      >
        {!hideChrome && user && (
          <>
            <WorkspaceRefresh />
            <div
              className="persistent-conversation"
              hidden={!isConversation && !isWorkspace}
            >
              <AIChatInterface active={isConversation || isWorkspace} />
            </div>
          </>
        )}
        <div
          className={
            isWorkspace
              ? "contextual-workspace"
              : isConversation
                ? "conversation-route"
                : ""
          }
        >
          {isWorkspace && (
            <header className="workspace-panel-bar">
              <span>{workspaceTitle(location)}</span>
              <div>
                <button
                  className="workspace-expand"
                  onClick={() => setExpanded(!expanded)}
                >
                  {expanded ? "Show conversation" : "Expand workspace"}
                </button>
                <button
                  onClick={() =>
                    window.dispatchEvent(new Event("timelyzer:search"))
                  }
                >
                  Search
                </button>
                <Link to="/ai">Back to conversation</Link>
              </div>
            </header>
          )}
          <Routes>
            {/* Public routes */}
            <Route path="/" element={<LandingPage />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />

            {/* Context-first product routes */}
            <Route
              path="/today"
              element={
                <ProtectedRoute>
                  <TodayPage />
                </ProtectedRoute>
              }
            />
            <Route path="/more" element={<Navigate to="/library" replace />} />
            <Route
              path="/library"
              element={
                <ProtectedRoute>
                  <WorkspaceMenu />
                </ProtectedRoute>
              }
            />
            <Route
              path="/goals"
              element={
                <ProtectedRoute>
                  <GoalsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/assistant"
              element={<Navigate to={`/manage${location.search}`} replace />}
            />
            <Route
              path="/manage"
              element={
                <ProtectedRoute>
                  <AssistantPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/preferences"
              element={
                <ProtectedRoute>
                  <AssistantPreferences />
                </ProtectedRoute>
              }
            />
            <Route
              path="/contexts"
              element={
                <ProtectedRoute>
                  <ContextsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/contexts/:contextId"
              element={
                <ProtectedRoute>
                  <ContextDetailPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/inbox"
              element={
                <ProtectedRoute>
                  <InboxPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/ai"
              element={
                <ProtectedRoute>
                  <WorkDock />
                </ProtectedRoute>
              }
            />

            {/* Supporting source and account views */}
            <Route
              path="/calendar"
              element={
                <ProtectedRoute>
                  <Calendar />
                </ProtectedRoute>
              }
            />
            <Route
              path="/meetings/:eventId"
              element={
                <ProtectedRoute>
                  <MeetingPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/calendar/events/:eventId"
              element={
                <ProtectedRoute>
                  <CalendarChangePage />
                </ProtectedRoute>
              }
            />
            <Route path="/maps-information" element={<MapsInformation />} />
            <Route
              path="/plans"
              element={
                <ProtectedRoute>
                  <PlansPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/plans/:planId"
              element={
                <ProtectedRoute>
                  <PlansPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/mail"
              element={
                <ProtectedRoute>
                  <MailPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/mail/threads/:threadId"
              element={
                <ProtectedRoute>
                  <MailPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/trackers"
              element={
                <ProtectedRoute>
                  <TrackerPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/trackers/:sourceId"
              element={
                <ProtectedRoute>
                  <TrackerPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/social"
              element={
                <ProtectedRoute>
                  <SocialPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/documents"
              element={
                <ProtectedRoute>
                  <DocumentsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/documents/:sourceId"
              element={
                <ProtectedRoute>
                  <DocumentsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/notes"
              element={
                <ProtectedRoute>
                  <NotesList />
                </ProtectedRoute>
              }
            />
            <Route
              path="/learning"
              element={
                <ProtectedRoute>
                  <LearningPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/challenges"
              element={
                <ProtectedRoute>
                  <ChallengesPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/challenges/:challengeId"
              element={
                <ProtectedRoute>
                  <ChallengesPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/integrations"
              element={
                <ProtectedRoute>
                  <GoogleIntegration />
                </ProtectedRoute>
              }
            />
            <Route
              path="/profile"
              element={
                <ProtectedRoute>
                  <ProfilePage />
                </ProtectedRoute>
              }
            />

            {/* Legacy redirects */}
            <Route path="/dashboard" element={<Navigate to="/ai" replace />} />
            <Route
              path="/dashboard/classic"
              element={<Navigate to="/ai" replace />}
            />
            <Route
              path="*"
              element={
                <div className="unified-page">
                  <h1>This workspace isn’t available.</h1>
                  <a href="/library">Open your library</a>
                </div>
              }
            />
          </Routes>
        </div>
      </div>

      {!hideChrome && user && (
        <>
          <BottomTabNav />
          <WorkspaceSearch />
        </>
      )}
      <UniversalCaptureSheet
        open={captureOpen}
        defaultContextId={captureContextId}
        onClose={() => {
          setCaptureOpen(false);
          setCaptureContextId(null);
        }}
      />
      <EventQuickCreateModal
        open={eventOpen}
        onClose={() => setEventOpen(false)}
        defaultDate={eventDate}
      />
      <NoteQuickCaptureModal
        open={noteOpen}
        onClose={() => setNoteOpen(false)}
      />

      {/* Toasts */}
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 4000,
          style: {
            border: "1px solid #dce3dd",
            borderRadius: "6px",
            background: "#17211c",
            color: "#fff",
            boxShadow: "0 14px 36px rgba(21, 36, 28, 0.16)",
          },
          success: { iconTheme: { primary: "#5fa783", secondary: "#fff" } },
          error: { iconTheme: { primary: "#df795d", secondary: "#fff" } },
        }}
      />
    </div>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <Router
          future={{ v7_startTransition: true, v7_relativeSplatPath: true }}
        >
          <Shell />
        </Router>
      </AuthProvider>
    </QueryClientProvider>
  );
}

export default App;
